import axios from 'axios'
import { useAuthStore } from '@/stores/auth'
import router from '@/router'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor - add auth token
api.interceptors.request.use(
  config => {
    const authStore = useAuthStore()
    if (authStore.token) {
      config.headers.Authorization = `Bearer ${authStore.token}`
    }
    return config
  },
  error => Promise.reject(error)
)

// Response interceptor - handle errors
api.interceptors.response.use(
  response => response.data,
  async error => {
    const config = error.config

    // Handle 503 (queue full) with automatic retry
    if (error.response?.status === 503 && !config._retry503) {
      config._retry503 = true
      const retryAfter = parseInt(error.response.headers['retry-after'] || '30', 10)
      // Cap retry wait at 60 seconds for UX, minimum 5 seconds
      const waitMs = Math.min(Math.max(retryAfter, 5), 60) * 1000

      console.warn(`Server at capacity (503). Retrying after ${retryAfter}s...`)
      await new Promise(resolve => setTimeout(resolve, waitMs))
      return api(config)
    }

    if (error.response?.status === 401) {
      const authStore = useAuthStore()
      authStore.logout()
      router.push({ name: 'login' })
    }

    // CD-395: Normalize error response format
    // Backend returns { error: { code, message } } but consumers expect { detail, code }
    const data = error.response?.data
    if (data?.error?.message) {
      return Promise.reject({
        detail: data.error.message,
        code: data.error.code,
        errors: data.error.errors,
        status: error.response?.status
      })
    }
    // CD-460: Detect timeout errors and provide user-friendly message.
    // Handles both frontend axios timeouts (ECONNABORTED) and nginx
    // gateway timeouts (504 status) which occur when the backend takes
    // longer than nginx's proxy_read_timeout to respond.
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.response?.status === 504) {
      return Promise.reject({
        detail: 'The request took too long. The server may still be processing your request. Please check back shortly.',
        code: 'TIMEOUT',
        status: error.response?.status || 408
      })
    }
    // CD-442: Ensure status is always available on error objects for proper handling
    // (e.g., DomainsView.vue checks err.status === 409 for duplicate scan detection)
    const errorObj = data || { detail: error.message || 'Request failed' }
    if (error.response?.status) {
      errorObj.status = error.response.status
    }
    return Promise.reject(errorObj)
  }
)

export default api
